import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import type { BlogState } from '../types';
import type { CreateBlogRequest, UpdateBlogRequest } from '../../types/api.types';
import { getBlogDetails, createBlog, updateBlog } from '../../services/blogService';

const initialState: BlogState = {
    currentBlog: null,
    loading: 'idle',
    saving: false,
    error: null,
};

// ============================================
// Async Thunks
// ============================================

export const fetchBlogDetails = createAsyncThunk(
    'blog/fetchDetails',
    async (blogId: string) => {
        return await getBlogDetails(blogId);
    }
);

export const saveBlogThunk = createAsyncThunk(
    'blog/save',
    async (payload: CreateBlogRequest | UpdateBlogRequest) => {
        const isCreate = !!(payload as CreateBlogRequest).title &&
            !(await isExistingBlog(payload._id));
        if (isCreate) {
            return await createBlog(payload as CreateBlogRequest);
        }
        await updateBlog(payload as UpdateBlogRequest);
        return null;
    }
);

export const createBlogThunk = createAsyncThunk(
    'blog/create',
    async (payload: CreateBlogRequest) => {
        return await createBlog(payload);
    }
);

export const updateBlogThunk = createAsyncThunk(
    'blog/update',
    async (payload: UpdateBlogRequest) => {
        await updateBlog(payload);
    }
);

// Helper — the page knows which thunk to call, so this is unused in practice
async function isExistingBlog(_id: string): Promise<boolean> {
    return !!_id;
}

// ============================================
// Slice
// ============================================

const blogSlice = createSlice({
    name: 'blog',
    initialState,
    reducers: {
        clearCurrentBlog: (state) => {
            state.currentBlog = null;
            state.error = null;
            state.loading = 'idle';
        },
        clearBlogError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch blog details
            .addCase(fetchBlogDetails.pending, (state) => {
                state.loading = 'loading';
                state.error = null;
            })
            .addCase(fetchBlogDetails.fulfilled, (state, action) => {
                state.loading = 'success';
                state.currentBlog = action.payload;
                state.error = null;
            })
            .addCase(fetchBlogDetails.rejected, (state, action) => {
                state.loading = 'error';
                state.error = action.error.message || 'Failed to fetch blog details';
            })

            // Create blog
            .addCase(createBlogThunk.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(createBlogThunk.fulfilled, (state) => {
                state.saving = false;
                state.error = null;
            })
            .addCase(createBlogThunk.rejected, (state, action) => {
                state.saving = false;
                state.error = action.error.message || 'Failed to create blog';
            })

            // Update blog
            .addCase(updateBlogThunk.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(updateBlogThunk.fulfilled, (state) => {
                state.saving = false;
                state.error = null;
            })
            .addCase(updateBlogThunk.rejected, (state, action) => {
                state.saving = false;
                state.error = action.error.message || 'Failed to update blog';
            });
    },
});

// ============================================
// Actions
// ============================================

export const { clearCurrentBlog, clearBlogError } = blogSlice.actions;

// ============================================
// Selectors (Custom Hooks)
// ============================================

export const useCurrentBlog = () => useSelector((state: RootState) => state.blog.currentBlog);

export const useBlogLoading = () => useSelector((state: RootState) => state.blog.loading);

export const useBlogSaving = () => useSelector((state: RootState) => state.blog.saving);

export const useBlogError = () => useSelector((state: RootState) => state.blog.error);

// ============================================
// Reducer
// ============================================

export default blogSlice.reducer;
